import styles from "./styles.module.scss";
import { Routes } from "routes";
import { useAppStore } from "store";
import { observer } from "mobx-react";
import CmsSpreadsheet from "../cms-spreadsheet/cms-spreadsheet";
import { Columns } from "./data";
import CmsTileCourse from "./cms-tile-course/cms-tile-course";
import { useBulkSelections } from "./useBulkSelections";
import { CmsCoursesFilter } from "./cms-courses-filter";
import { PermissionConstantsRead } from "models/dto/ZoomiLxp/Utilities/Constants/PermissionConstantsRead";
import Button from "components/base/button/button";
import { modalDialogService } from "components/base/modal-dialog/modal-dialog";
import { AddCoursesModal } from "./add-courses-modal";
import { useCallback, useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { ICourseModel } from "models/dto/ZoomiLxp/Models/Courses/ICourseModel";
import { IGetRecordsResponse } from "models/dto/ZoomiLxp/Models/Common/IGetRecordsResponse";
import { ICmsCourseModel } from "models/dto/ZoomiLxp/Models/Courses/ICmsCourseModel";
import { Prompt } from "react-router";
import { infoMessages } from "constants/messages";

interface ICmsCoursesProps {
	isHideFilter?: boolean;
	enableGroupActions?: boolean;
}

type MoveDirection = "up" | "down";

const moveItem = <T,>(items: T[], fromIndex: number, toIndex: number) => {
	if (fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) return items;

	const nextItems = [...items];
	const [item] = nextItems.splice(fromIndex, 1);
	nextItems.splice(toIndex, 0, item);
	return nextItems;
};

const toCmsCourse = (course: ICourseModel): ICmsCourseModel => ({
	id: course.id,
	title: course.title,
	description: course.description,
	price: course.price,
	isActive: course.isActive,
	lastUpdated: course.lastUpdated,
	updatedUtc: course.lastUpdated,
	thumbnail: course.thumbnail!,
});

const CmsCourses = ({ isHideFilter, enableGroupActions }: ICmsCoursesProps) => {
	const { coursesStore, bulkOperationsStore, searchStore, groupStore, toasterStore } = useAppStore();
	const [initialCourseIds, setInitialCourseIds] = useState<number[]>([]);
	const [draftCourseIds, setDraftCourseIds] = useState<number[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const isGridChanged = !isEqual(initialCourseIds, draftCourseIds);

	useEffect(() => {
		if (!enableGroupActions) return;

		const courseIds = groupStore.groupData.courses.map(({ id }) => id);
		setInitialCourseIds(courseIds);
		setDraftCourseIds(courseIds);
	}, [enableGroupActions, groupStore.groupData.id, groupStore.groupData.courses]);

	useEffect(() => {
		if (!enableGroupActions || isGridChanged || isEmpty(searchStore.items)) return;

		const courseIds = searchStore.items.map(({ id }) => id);
		setInitialCourseIds(courseIds);
		setDraftCourseIds(courseIds);
	}, [enableGroupActions, isGridChanged, searchStore.items]);

	const handleRemoveCourse = (courseId: number) => {
		setDraftCourseIds((courseIds) => courseIds.filter((id) => id !== courseId));
		searchStore.items = searchStore.items.filter(({ id }) => id !== courseId);
	};

	const handleMoveCourse = (courseId: number, direction: MoveDirection) => {
		const currentIndex = searchStore.items.findIndex(({ id }) => id === courseId);
		const nextIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
		const nextItems = moveItem(searchStore.items, currentIndex, nextIndex);

		if (nextItems === searchStore.items) return;

		searchStore.items = nextItems;
		setDraftCourseIds(nextItems.map(({ id }) => id));
	};

	const getColumns = () => {
		const columns = Columns(
			enableGroupActions ? handleRemoveCourse : undefined,
			enableGroupActions ? handleMoveCourse : undefined
		);
		if (bulkOperationsStore.isShowSelection) return columns;

		return columns[0].columns.filter((column) => column.id !== "selection");
	};

	useBulkSelections([
		PermissionConstantsRead.OwnCoursesForTechActions,
		PermissionConstantsRead.AllCoursesForTechActions,
	]);

	const getGroupCourses = useCallback(async (): Promise<IGetRecordsResponse<ICmsCourseModel>> => {
		const group = await groupStore.getGroupData(groupStore.groupData.id);
		const records = (group?.courses ?? []).map(toCmsCourse);

		return {
			records,
			totalFilteredRecords: records.length,
		};
	}, [groupStore]);

	const fetchCoursesData = useCallback(
		() => (enableGroupActions ? getGroupCourses() : coursesStore.getCMSCourses()),
		[enableGroupActions, getGroupCourses, coursesStore]
	);

	const handleAddCourses = async () => {
		const selectedCourses = await modalDialogService.show(AddCoursesModal, {
			excludedCourseIds: draftCourseIds,
		});

		if (isEmpty(selectedCourses)) return;

		setDraftCourseIds((courseIds) => [
			...courseIds,
			...selectedCourses!.map(({ id }) => id).filter((id) => !courseIds.includes(id)),
		]);
		searchStore.items = [...searchStore.items, ...selectedCourses!] as any;
	};

	const handleSaveCourses = async () => {
		if (!isGridChanged) return;

		setIsSaving(true);
		try {
			await groupStore.setGroupCourses(draftCourseIds);
			setInitialCourseIds(draftCourseIds);
			await searchStore.getTableInitialData(fetchCoursesData as any);
			toasterStore.showSuccessMessage("Courses saved");
		} catch (err) {
			toasterStore.showErrorMessage(err);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className={styles.cms_courses}>
			{enableGroupActions && <Prompt message={infoMessages.leaveWithoutSaving} when={isGridChanged} />}
			{!isHideFilter && <CmsCoursesFilter />}
			{enableGroupActions && (
				<div className={styles.cms_courses__group_actions}>
					<Button label="Add" onClick={handleAddCourses} className={styles.cms_courses__group_action_btn} />
					<Button
						label="Save"
						onClick={handleSaveCourses}
						disabled={!isGridChanged || isSaving}
						className={styles.cms_courses__group_action_btn}
					/>
				</div>
			)}
			<CmsSpreadsheet
				tableColumns={getColumns()}
				tileComponent={CmsTileCourse}
				fetchData={fetchCoursesData}
				profileRoute={Routes.CmsCourseProfile}
				className={styles.cms_courses__body}
			/>
		</div>
	);
};

export default observer(CmsCourses);
